import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Loader2, X } from "lucide-react";
import { api, formatApiError, slugify, img } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { SingleImage, UploadButton } from "@/pages/admin/ImageManager";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { SpanishFields, cleanEs } from "@/pages/admin/SpanishFields";
import { AiWriter } from "@/pages/admin/AiWriter";

const today = () => new Date().toISOString().slice(0, 10);
const BLANK = { slug: "", title: "", excerpt: "", content: "", cover_image: "", video_url: "", video_file: "", keywords: [], seo_title: "", seo_description: "", author: "Escamequita", published: true, published_at: today(), created_at: "", es: {} };
const ES_FIELDS = [
  { key: "title", label: "Título", type: "input" }, { key: "excerpt", label: "Resumen", rows: 2 },
  { key: "content", label: "Contenido", rows: 14 }, { key: "seo_title", label: "Título SEO", type: "input" }, { key: "seo_description", label: "Descripción SEO", rows: 2 },
];

const Field = ({ label, hint, children }) => (
  <div className="space-y-2"><Label className="font-bold text-ink">{label}</Label>{children}{hint && <p className="text-xs text-moss">{hint}</p>}</div>
);

const MediaFields = ({ f, setF }) => (
  <div className="space-y-5 rounded-2xl border border-clay bg-white p-6 sm:p-8" data-testid="post-media">
    <h2 className="text-xl font-black text-forest">Image & video</h2>
    <Field label="Cover image"><SingleImage value={f.cover_image} onChange={(v) => setF({ ...f, cover_image: v })} testId="post-cover-upload" /></Field>
    <Field label="Video file (mp4, mov, webm — up to 150MB)">
      <div className="flex flex-wrap items-center gap-3">
        <UploadButton accept="video/mp4,video/webm,video/quicktime" onUrls={(u) => setF({ ...f, video_file: u[0] })} testId="post-video-upload" label="Upload video" />
        {f.video_file && (
          <>
            <video src={img(f.video_file)} className="h-20 rounded-lg bg-ink" muted data-testid="post-video-preview" />
            <button type="button" onClick={() => setF({ ...f, video_file: "" })} className="text-destructive" aria-label="Remove video" data-testid="post-video-remove"><X className="h-4 w-4" /></button>
          </>
        )}
      </div>
    </Field>
    <Field label="…or YouTube link" hint="A YouTube link is shown instead of the uploaded video if both are set">
      <Input value={f.video_url} onChange={(e) => setF({ ...f, video_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" data-testid="post-youtube" />
    </Field>
  </div>
);

export default function PostEditor() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const isNew = id === "new";
  const nav = useNavigate();
  const [f, setF] = useState(isNew ? BLANK : null);
  const [kw, setKw] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isNew) {
      api.get(`/admin/posts/${id}`).then((r) => { setF(r.data); setKw(r.data.keywords.join(", ")); }).catch((e) => toast.error(formatApiError(e)));
    }
  }, [id, isNew]);
  if (!f) return <p className="text-moss">Loading…</p>;
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const applyAi = (r) => {
    setF((cur) => ({ ...cur, ...Object.fromEntries(Object.entries(r).filter(([k, v]) => (Array.isArray(v) ? v.length : v) && k !== "keywords")), slug: isNew ? slugify(r.slug || r.title) : cur.slug, es: {} }));
    if (r.keywords.length) setKw(r.keywords.join(", "));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...f, slug: slugify(f.slug || f.title), keywords: kw.split(",").map((s) => s.trim()).filter(Boolean), es: cleanEs(f.es) };
    try {
      if (isNew) await api.post("/admin/posts", body);
      else await api.put(`/admin/posts/${id}`, body);
      toast.success("Story saved — Spanish version will be ready in about 30 seconds");
      nav("/admin/posts");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-4xl space-y-6" data-testid="post-editor">
      <Link to="/admin/posts" className="inline-flex items-center gap-1 text-sm font-bold text-moss hover:text-forest"><ChevronLeft className="h-4 w-4" /> Back</Link>
      <AdminHeader title={isNew ? "New story" : `Edit: ${f.title}`}>
        <button type="submit" disabled={saving} className="btn-primary" data-testid="post-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save story</button>
      </AdminHeader>
      <AiWriter onResult={applyAi} defaultOpen={params.get("ai") === "1"} />
      <div className="space-y-5 rounded-2xl border border-clay bg-white p-6 sm:p-8">
        <h2 className="text-xl font-black text-forest">Story</h2>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Title"><Input required value={f.title} onChange={set("title")} data-testid="post-title" /></Field>
          <Field label="URL slug" hint={`Page address: /stories/${slugify(f.slug || f.title) || "…"}`}><Input value={f.slug} onChange={set("slug")} placeholder="auto from title" data-testid="post-slug" /></Field>
          <Field label="Publish date" hint="Leave empty to hide the date"><Input type="date" value={f.published_at?.slice(0, 10) || ""} onChange={set("published_at")} data-testid="post-date" /></Field>
          <label className="flex items-center gap-3 pt-7 font-bold"><Switch checked={f.published} onCheckedChange={set("published")} data-testid="post-published" /> Published</label>
        </div>
        <Field label="Excerpt (shown on cards)"><Textarea rows={2} value={f.excerpt} onChange={set("excerpt")} data-testid="post-excerpt" /></Field>
        <Field label="Content" hint='Blank line = new paragraph. "## " heading, "- " bullet, **bold**, [link text](/for-rent), ![photo](image-url) for an inline image.'>
          <Textarea rows={18} value={f.content} onChange={set("content")} data-testid="post-content" />
        </Field>
      </div>
      <MediaFields f={f} setF={setF} />
      <div className="space-y-5 rounded-2xl border border-clay bg-white p-6 sm:p-8">
        <h2 className="text-xl font-black text-forest">SEO</h2>
        <Field label="Keywords" hint="Comma separated"><Input value={kw} onChange={(e) => setKw(e.target.value)} data-testid="post-keywords" /></Field>
        <Field label="SEO title"><Input value={f.seo_title} onChange={set("seo_title")} data-testid="post-seo-title" /></Field>
        <Field label="SEO description" hint="~155 characters"><Textarea rows={2} value={f.seo_description} onChange={set("seo_description")} data-testid="post-seo-description" /></Field>
      </div>
      <SpanishFields fields={ES_FIELDS} source={f} value={f.es} onChange={(es) => setF({ ...f, es })} testId="post-es" />
    </form>
  );
}
